import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Loader2, AlertTriangle, ShieldAlert, CircleDollarSign, Flame, MessageCircle } from "lucide-react";

const ICONS = {
  low_pool: Flame,
  fraud_queue: ShieldAlert,
  failed_payouts: CircleDollarSign,
  budget_exhausted: AlertTriangle,
  nps_detractors: AlertTriangle,
  unknown_inbound: MessageCircle,
};
const TONE = {
  high: "border-l-4 border-[#c01974] bg-[#fff0f5]",
  medium: "border-l-4 border-[#a87700] bg-[#fff7e6]",
  low: "border-l-4 border-[#5c665a] bg-[#fafcfa]",
};

export default function Alerts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/alerts");
      setData(data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-[#1a1c19] sm:text-3xl flex items-center gap-2">
            <Bell className="h-6 w-6 text-[#ff3399]" /> Alerts
          </h1>
          <p className="text-sm text-[#5c665a]">
            Items where a human agent may need to step in. Otherwise the program runs fully automated.
          </p>
        </div>
        <button className="admin-btn-ghost" onClick={load} data-testid="alerts-refresh">Refresh</button>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#66cc33]" /></div>
      ) : !data || data.count === 0 ? (
        <div className="admin-card text-center" data-testid="alerts-empty">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#eaf8de]">
            <Bell className="h-6 w-6 text-[#3f7c21]" />
          </div>
          <div className="text-sm font-bold text-[#1a1c19]">All clear. No intervention needed right now.</div>
          <div className="text-xs text-[#5c665a]">MYDAWA Refer &amp; Earn is running smoothly on autopilot.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="alerts-list">
          {data.alerts.map((a, i) => {
            const Icon = ICONS[a.type] || AlertTriangle;
            return (
              <div key={i} className={`flex items-start gap-3 rounded-xl p-4 ${TONE[a.severity] || TONE.low}`} data-testid={`alert-${a.type}`}>
                <Icon className="mt-0.5 h-5 w-5 text-[#1a1c19]" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[#1a1c19]">{a.title}</span>
                    <span className="rounded-full bg-white/60 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">{a.severity}</span>
                  </div>
                  <div className="mt-1 text-xs text-[#5c665a]">{a.body}</div>
                </div>
                {a.action_path && (
                  <button onClick={() => nav(a.action_path)} className="admin-btn-ghost shrink-0">Take action</button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
